/**
 * Reports > Customer satisfaction (tracker item 69): the average rating,
 * response rate, how the scores are spread, the trend by week or month and
 * the scores per technician, from the customer surveys. Dates are UTC days;
 * the response rate counts the surveys sent in the range and how many of
 * those were answered. ADMIN and OFFICE_MANAGER.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { createAPIClient } from '../../api';
import * as types from '../../api/types';
import { StarDisplay } from '../../components/StarRating';
import { cellClass, DateField, EmptyRow, FilterGrid, RangeError, ReportCard, ReportPage, ReportStatus, ReportTable, SelectField, SummaryTiles, useReportData, useReportParams } from './ReportParts';
import { dateParam, dateRangeError } from './reportUi';
import { averageText, rateText, SURVEYS_PATH } from '../surveys/surveyUi';

const DISTRIBUTION_COLUMNS = [{ label: 'Rating' }, { label: 'Answers', numeric: true }, { label: 'Share', numeric: true }, { label: '' }];
const TREND_COLUMNS = [{ label: 'Period' }, { label: 'Answers', numeric: true }, { label: 'Average', numeric: true }, { label: '' }];
const TECHNICIAN_COLUMNS = [{ label: 'Technician' }, { label: 'Answers', numeric: true }, { label: 'Average', numeric: true }, { label: 'Low scores', numeric: true }];

/** A horizontal bar for a value out of a maximum (decorative: the number beside it is the data). */
function Bar({ value, max, tone = 'bg-blue-500' }: { value: number; max: number; tone?: string }) {
  const width = max > 0 ? Math.max(0, Math.min(100, (value / max) * 100)) : 0;
  return (
    <div aria-hidden="true" className="h-3 w-full min-w-[80px] rounded bg-gray-100">
      <div className={`h-3 rounded ${tone}`} style={{ width: `${width}%` }} />
    </div>
  );
}

export function CustomerSatisfactionReportPage() {
  const api = useMemo(() => createAPIClient(), []);
  const { params, change } = useReportParams();
  const startDate = dateParam(params.get('startDate'));
  const endDate = dateParam(params.get('endDate'));
  const technicianId = params.get('technicianId') || '';
  const groupBy: types.SatisfactionGroupBy = params.get('groupBy') === 'month' ? 'month' : 'week';
  const rangeError = dateRangeError(startDate, endDate);
  const [staff, setStaff] = useState<types.UserListItem[]>([]);

  useEffect(() => {
    let active = true;
    api.listUsers(true).then((users) => { if (active) setStaff(users.filter((u) => u.role !== types.UserRole.CUSTOMER_PORTAL)); }).catch(() => { /* the filter just stays empty */ });
    return () => { active = false; };
  }, [api]);

  const query: types.SatisfactionReportQuery = {
    startDate: startDate || undefined,
    endDate: endDate || undefined,
    technicianId: technicianId || undefined,
    groupBy,
  };
  const state = useReportData(rangeError ? null : JSON.stringify(query), () => api.getSatisfactionReport(query), 'Could not load the customer satisfaction report');

  return (
    <ReportPage
      title="Customer satisfaction"
      intro={
        <>
          How customers rated finished visits, from the <Link to={SURVEYS_PATH} className="text-blue-700 hover:underline">customer surveys</Link>. Without dates it covers the last 90 days.
          The response rate is the share of the surveys sent in the period that were answered.
        </>
      }
    >
      <FilterGrid>
        <DateField id="sat-start" label="Answered from" value={startDate} onChange={(value) => change({ startDate: value })} />
        <DateField id="sat-end" label="Answered to" value={endDate} onChange={(value) => change({ endDate: value })} />
        <SelectField id="sat-technician" label="Technician" value={technicianId} onChange={(value) => change({ technicianId: value })}>
          <option value="">All technicians</option>
          {technicianId && !staff.some((u) => u.id === technicianId) && <option value={technicianId}>Selected technician</option>}
          {staff.map((u) => <option key={u.id} value={u.id}>{u.email}</option>)}
        </SelectField>
        <SelectField id="sat-group" label="Trend by" value={groupBy} onChange={(value) => change({ groupBy: value === 'month' ? 'month' : '' })}>
          <option value="week">Week</option>
          <option value="month">Month</option>
        </SelectField>
      </FilterGrid>
      <RangeError message={rangeError} />
      <ReportStatus state={state} label="customer satisfaction report">
        {(report) => {
          const maxCount = Math.max(0, ...report.distribution.map((d) => d.count));
          const maxTrend = Math.max(0, ...report.trend.map((t) => t.responses));
          return (
            <>
              <p className="text-sm text-gray-600" data-testid="satisfaction-period">Period: {report.startDate} to {report.endDate}</p>
              <SummaryTiles
                tiles={[
                  { label: 'Average rating', value: report.summary.averageRating === null ? '—' : <span>{averageText(report.summary.averageRating)} <span className="text-base font-normal text-gray-500">of 5</span></span> },
                  { label: 'Answers', value: report.summary.responses },
                  { label: 'Response rate', value: <span>{rateText(report.summary.responseRate)}<span className="block text-xs font-normal text-gray-500">{report.summary.requestsAnswered} of {report.summary.requestsSent} sent</span></span> },
                  { label: `Low scores (${report.threshold} or fewer)`, value: report.summary.lowScoreCount, tone: report.summary.lowScoreCount > 0 ? 'bad' : undefined },
                ]}
              />
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                <ReportCard title="How the scores are spread">
                  <ReportTable columns={DISTRIBUTION_COLUMNS} caption="Answers by rating">
                    {report.distribution.map((row) => (
                      <tr key={row.rating}>
                        <td className={cellClass()} data-label="Rating" data-primary><StarDisplay rating={row.rating} /> <span className="ml-1">{row.rating} - {row.label}</span></td>
                        <td className={cellClass(true)} data-label="Answers">{row.count}</td>
                        <td className={cellClass(true)} data-label="Share">{row.percent}%</td>
                        <td className={`${cellClass()} w-1/3`}><Bar value={row.count} max={maxCount} tone={row.rating <= report.threshold ? 'bg-red-500' : 'bg-blue-500'} /></td>
                      </tr>
                    ))}
                  </ReportTable>
                </ReportCard>
                <ReportCard title={`Trend by ${report.groupBy}`}>
                  <ReportTable columns={TREND_COLUMNS} caption={`Average rating by ${report.groupBy}`}>
                    {report.trend.length === 0 ? <EmptyRow colSpan={TREND_COLUMNS.length} message="No answers in this period." /> : report.trend.map((row) => (
                      <tr key={row.periodStart}>
                        <td className={cellClass()} data-label="Period" data-primary>{row.label}</td>
                        <td className={cellClass(true)} data-label="Answers">{row.responses}</td>
                        <td className={cellClass(true)} data-label="Average">{averageText(row.averageRating)}</td>
                        <td className={`${cellClass()} w-1/3`}><Bar value={row.averageRating} max={5} tone="bg-green-500" /></td>
                      </tr>
                    ))}
                  </ReportTable>
                  {report.trend.length > 0 && <p className="mt-2 text-xs text-gray-500">Bars show the average out of 5 (busiest period: {maxTrend} answers).</p>}
                </ReportCard>
              </div>
              <ReportCard title="By technician">
                <ReportTable columns={TECHNICIAN_COLUMNS} caption="Ratings by technician">
                  {report.byTechnician.length === 0 ? <EmptyRow colSpan={TECHNICIAN_COLUMNS.length} message="No answers in this period." /> : report.byTechnician.map((row) => (
                    <tr key={row.technicianId ?? 'unassigned'}>
                      <td className={`${cellClass()} break-words`} data-label="Technician" data-primary>
                        {row.technicianId ? (
                          <button type="button" className="text-left text-blue-700 hover:underline" onClick={() => change({ technicianId: row.technicianId as string })}>{row.name}</button>
                        ) : row.name}
                      </td>
                      <td className={cellClass(true)} data-label="Answers">{row.responses}</td>
                      <td className={cellClass(true)} data-label="Average">{averageText(row.averageRating)}</td>
                      <td className={cellClass(true)} data-label="Low scores">{row.lowScoreCount}</td>
                    </tr>
                  ))}
                </ReportTable>
              </ReportCard>
            </>
          );
        }}
      </ReportStatus>
    </ReportPage>
  );
}
